import moment from "moment"

const TIME_RANGE_FORMAT = "YYYY-MM-DD H:mm"
const TIME_RANGE_FORMAT_WITH_TZ = `${TIME_RANGE_FORMAT} ZZ`

const ABSOLUTE_INPUT = /^(\d{4}[- ]\d{1,2}[- ]\d{1,2}\s+\d{1,2}:\d{2})\s?-\s?(\d{4}[- ]\d{1,2}[- ]\d{1,2} \d{1,2}:\d{2})$/
const RELATIVE_INPUT = /^(\d+)([A-z]{1,2})$/i

// Need to define keys like "ss", "s", "mm", "m", etc. that all have the same
// format string
// https://momentjs.com/docs/#/customization/relative-time-threshold/
moment.defineLocale("en-shorthand", {
  parentLocale: "en",
  relativeTime: {
    s:"%ds", ss:"%ds",
    m:"%dm", mm:"%dm",
    h:"%dh", hh:"%dh",
    d:"%dd", dd:"%dd",
    w:"%dw", ww:"%dw",
    M:"%dmo", MM:"%dmo",
    y:"%dy", yy:"%dy"
  }
})

const UNITS = {
  s: "second", m: "minute", h: "hour", d: "day", w: "week", mo: "month", y: "year"
}

// Parse absolute input with 2 timestamps
function parseAbsoluteInput(value, tzOffset) {
  const match = value.match(ABSOLUTE_INPUT)

  if (match === null) {
    return
  }

  let start = match[1]
  let stop = match[2]

  if (tzOffset) {
    start = moment.parseZone(`${start.trim()} ${tzOffset}`, TIME_RANGE_FORMAT_WITH_TZ).utc()
    stop = moment.parseZone(`${stop.trim()} ${tzOffset}`, TIME_RANGE_FORMAT_WITH_TZ).utc()
  } else {
    start = moment(start.trim(), TIME_RANGE_FORMAT)
    stop = moment(stop.trim(), TIME_RANGE_FORMAT)
  }

  return [start, stop]
}

// Parse relative input (ie: 5m, 6h, 4d, 2w, 3mo)
function parseRelativeInput(value) {
  const match = value.match(RELATIVE_INPUT)

  if (match === null) {
    return
  }

  const amount = match[1]
  const unitAbbrv = match[2]
  const unit = UNITS[unitAbbrv.toLowerCase()]

  if (unit === null) {
    return
  }

  return [moment().subtract(+amount, unit), moment()]
}

function createChangeEvent(detail) {
  if (!detail) detail = {}
  //if (!detail.valid) detail.valid = true

  return new CustomEvent("changed", {
    detail: detail
  })
}

export class TimeRangePickerState extends EventTarget {
  constructor(start, stop, tzOffset) {
    super()
    this.start = moment(start, true)
    this.stop = moment(stop, true)
    this.tzOffset = tzOffset
  }

  updateFromTextInput(value) {
    let parsed, mode = "absolute"

    parsed = parseAbsoluteInput(value, this.tzOffset)

    console.log(parsed)

    if (!parsed) {
      parsed = parseRelativeInput(value)
      mode = "relative"
    }

    if (!parsed) {
      return false
    }

    const [start, stop] = parsed

    if (stop.isBefore(start)) {
      return false
    } else if (!start.isValid() || !stop.isValid()) {
      return false
    }

    if (this.start.isSame(start) && this.stop.isSame(stop)) {
      return true
    }

    this.start = start
    this.stop = stop
    this.dispatchEvent(createChangeEvent({ mode }))

    return true
  }

  updateFromRelativeRangeOption(amount, unit) {
    const stop = moment()
    const start = moment().subtract(amount, unit)

    if (start.isValid() && stop.isValid()) {
      this.start = start
      this.stop = stop
      this.dispatchEvent(createChangeEvent())
    }
  }

  updateStartDate(start) {
    this.start = this.start.year(start.year()).month(start.month()).date(start.date())
    this.dispatchEvent(createChangeEvent())

    return true
  }

  updateStopDate(stop) {
    this.stop = this.stop.year(stop.year()).month(stop.month()).date(stop.date())
    this.dispatchEvent(createChangeEvent())

    return true
  }

  isValid() {
    return this.start.isValid() && this.stop.isValid()
  }

  isStartDaySame(date) {
    return this.start.isSame(date, "day")
  }

  isStopDaySame(date) {
    return this.stop.isSame(date, "day")
  }

  toInputValues() {
    return [this.start.utc().toISOString(), this.stop.utc().toISOString()]
  }

  toDateValues() {
    return [this.start.local().toDate(), this.stop.local().toDate()]
  }

  toDurationString() {
    return moment.duration(this.stop.diff(this.start)).locale("en-shorthand")
  }

  toLocalizedString() {
    if (this.tzOffset) {
      return `${this.start.utcOffset(this.tzOffset).format(TIME_RANGE_FORMAT)} - ${this.stop.utcOffset(this.tzOffset).format(TIME_RANGE_FORMAT)}`
    } else {
      return `${this.start.local().format(TIME_RANGE_FORMAT)} - ${this.stop.local().format(TIME_RANGE_FORMAT)}`
    }
  }
}
